import {
    AfterContentInit,
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ContentChild,
    ContentChildren,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    Optional,
    Output,
    QueryList,
    SimpleChanges,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { AbstractControl, AsyncValidatorFn, ControlValueAccessor, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { BehaviorSubject, Subject } from 'rxjs';

import { MatAutocomplete, MatAutocompleteActivatedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { takeUntil } from 'rxjs/operators';
import { IField } from '../../../../models/field/field.model';
import { GenericFieldAddonDirective } from '../../generic-directives/generic-field-addon.directive';
import { GenericFieldDirective } from '../../generic-directives/generic-field.directive';
import { genericAutocompleteInputHandleKeydown } from '../generic-text-input/mat-autocomplete-trigger';

@Component({
    selector: 'mat-base-input',
    template: '',
})
export class BaseGenericInputComponent<T> implements ControlValueAccessor, AfterContentInit, AfterViewInit, OnChanges {

    @ContentChildren(GenericFieldAddonDirective)
    public fieldAddonDirectives: QueryList<GenericFieldAddonDirective>;

    @ContentChild(GenericFieldDirective)
    public fieldDirective: GenericFieldDirective;

    @Input()
    public initialValue: T;

    @Input()
    public field: IField;

    @Input()
    public inputFormControl: FormControl | AbstractControl;

    /**
     * Is not supported, please use inputFormControl instead
     */
    @Input()
    public formControlName: string;

    @Input()
    public index: number = 1;

    @Input()
    public placeholder: string;

    @Input()
    public disabled: boolean;

    @Input()
    public readonly: boolean;

    @Input()
    public fieldTemplate: GenericFieldDirective;

    @Input()
    public fieldAddOnTemplates: TemplateRef<unknown>[];

    @Output()
    public blur: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();

    @Output()
    public focus: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();

    @Output()
    public focusin: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();

    @Output()
    public focusout: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();

    @ViewChild(MatAutocompleteTrigger)
    public set matAutocompleteTrigger(autocompleteTrigger: MatAutocompleteTrigger) {
        if (autocompleteTrigger) {
            autocompleteTrigger._handleKeydown = genericAutocompleteInputHandleKeydown((_event: KeyboardEvent) => {
                this.selectAutocompleteValue(this.activeOption, true);
            });
        }
    }

    @ViewChild(MatAutocomplete)
    public set matAutocomplete(autocomplete: MatAutocomplete) {
        if (autocomplete) {
            autocomplete.optionActivated
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((a: MatAutocompleteActivatedEvent) => {
                    if (a.option) {
                        this.activeOption = a.option.value;
                    }
                });
        }
    }

    @HostListener('document:click')
    @HostListener('document:focusin')
    @HostListener('document:focusout')
    @HostListener('window:keyup')
    public click: () => void = this.onClick;

    // public customFormControl: FormControl<T>;

    public fieldAddonTemplates$: BehaviorSubject<TemplateRef<unknown>[]> = new BehaviorSubject([]);
    protected ngUnsubscribe: Subject<void> = new Subject<void>();
    protected activeOption: T;

    constructor(
        @Optional()
        protected myChangeDetectorRef: ChangeDetectorRef,
    ) {

    }

    public ngOnInit(): void {
        if (!this.inputFormControl) {
            this.inputFormControl = new FormControl<T>(this.initialValue != null && (this.initialValue as unknown as string) !== '' ? this.initialValue : null);
            // this.inputFormControl.valueChanges.subscribe((value: T) => this.onChange(value));
        }
        // this.customFormControl = new FormControl<T>(null);
    }

    public ngAfterViewInit(): void {
        /* if (this.initialValue != null && (this.initialValue as unknown as string) !== '') {
            this.writeValue(this.initialValue);
            // To prevent broken overlay
            this.myChangeDetectorRef.detectChanges();
        } */
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes[ 'disabled' ] && changes[ 'disabled' ].previousValue !== changes[ 'disabled' ].currentValue) {
            this.field.disabled = changes[ 'disabled' ].currentValue;
        }

        if (changes[ 'readonly' ] && changes[ 'readonly' ].previousValue !== changes[ 'readonly' ].currentValue) {
            this.field.readonly = changes[ 'readonly' ].currentValue;
        }

        if (changes[ 'formControlName' ]) {
            console.warn('formControlName is not supported, please use inputFormControl instead');
        }

        if (changes[ 'field' ] && changes[ 'field' ].firstChange) {
            if (!this.field) {
                console.warn('field is required');
            }

            if (!this.inputFormControl) {
                console.warn('inputFormControl is required');
            }

            const validators: (ValidatorFn | AsyncValidatorFn)[] = [ ...(this.field.validatorFn || []) ];
            if (this.field.required) {
                validators.push(Validators.required);
            }

            if (this.field.disabled) {
                this.inputFormControl.disable({ emitEvent: false });
            }

            this.inputFormControl.setAsyncValidators(this.field.asyncValidatorFn);
            this.inputFormControl.setValidators(validators);
        }

        // if (changes[ 'inputFormGroup' ] && changes[ 'inputFormGroup' ].previousValue !== changes[ 'inputFormGroup' ].currentValue) {
        //     this.customFormControl.setParent(changes[ 'inputFormGroup' ].currentValue);
        // }

        if (changes[ 'fieldAddOnTemplates' ] && changes[ 'fieldAddOnTemplates' ].firstChange && changes[ 'fieldAddOnTemplates' ].currentValue) {
            this.fieldAddonTemplates$.next(changes[ 'fieldAddOnTemplates' ].currentValue);
        }
    }

    public ngAfterContentInit(): void {
        if (this.fieldAddonDirectives.length > 0) {
            this.fieldAddonDirectives.forEach((fieldAddon: GenericFieldAddonDirective) => {
                const template: TemplateRef<unknown> = fieldAddon.template;
                const matAddonField: string | string[] = fieldAddon.matAddonField;
                if (matAddonField instanceof Array) {
                    for (const field of matAddonField) {
                        if (!this.fieldAddonTemplates$.value[ field ]) {
                            this.fieldAddonTemplates$.next([]);
                        }
                        this.fieldAddonTemplates$.value.push(template);
                    }
                } else if (matAddonField) {
                    if (!this.fieldAddonTemplates$.value) {
                        this.fieldAddonTemplates$.next([]);
                    }
                    this.fieldAddonTemplates$.value.push(template);
                } else {
                    if (!this.fieldAddonTemplates$.value) {
                        this.fieldAddonTemplates$.next([]);
                    }
                    this.fieldAddonTemplates$.value.push(template);
                }
            });
            this.fieldAddonTemplates$.next(this.fieldAddonTemplates$.value);
        }

        if (this.fieldDirective) {
            if (this.fieldDirective.matField instanceof Array) {
                /* for (const field of this.fieldDirective.matField) {
                    this.fieldTemplates[ field ] = this.fieldDirective;
                } */
            } else {
                this.fieldTemplate = this.fieldDirective;
            }
        }
    }

    public ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    public displayWith(field: IField, formControl: AbstractControl): (data: unknown) => string {
        if (!field.selectSearchData?.displayWith) {
            return;
        }
        return (data: unknown) => field.selectSearchData.displayWith(data, formControl as FormControl);
    }

    public selectAutocompleteValue(data: T | MatAutocomplete, onEnter?: boolean): void {
        if (onEnter && !this.activeOption) {
            return;
        }

        const value: T = (onEnter ? this.activeOption : data) as T;

        (this.field.selectSearchData.selectData.options as BehaviorSubject<T[]>).next([]);
        (this.field.selectSearchData.selected as BehaviorSubject<T[]>).next(null);
        if (this.field.selectSearchData.selectFunction) {
            this.field.selectSearchData.selectFunction(value, this.inputFormControl.parent as FormGroup);
            this.activeOption = null;
        } else {
            this.inputFormControl.setValue(value[ this.field.selectSearchData?.selectData?.valueKey ]);
            this.activeOption = null;
        }
    }

    public registerOnChange(fn: (value: T) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        isDisabled ? this.inputFormControl.disable({ emitEvent: false }) : this.inputFormControl.enable({ emitEvent: false });
    }

    public writeValue(value: T | T[]): void {
        this.inputFormControl.patchValue(value as T);
    }

    protected onChange: (value: T | T[]) => void = (_: T | T[]) => null;
    protected onTouched: () => void = () => null;

    private onClick(): void {
        this.myChangeDetectorRef?.detectChanges();
        setTimeout(() => this.myChangeDetectorRef?.detectChanges());
    }

}
